import PartnerLink from '@/schemes/partner-link'
import SchemeHost from '@/schemes/scheme-host'
import { SERVICE_OFFERINGS, FEATURES } from '@/schemes/scheme-config'

/*
 * Standard shared config should go here.
 * This should not be imported directly into a Vue component - It is accessible directly via Vue.$config.
 */

const baseUrl = '/'


// noinspection SpellCheckingInspection
/**
 * Application configuration
 */
export default {

    baseUrl: baseUrl,
    routerBaseUrl: '/',

    /**
     * Querystring key used to track partner referrals.
     * @type {string}
     */
    partnerReferralKey: 'partref',

    /**
     * Provides a number of retry attempts to use and an interval, in seconds, between them.
     * @type {{ interval: number, attempts: number }}
     */
    apiPolling: {
        interval: 3,
        attempts: 60,
    },

    analyticsDebug: false,
    analyticsIds: {
        default: null, // Disable Analytics
        dev: 'G-95KJEVGVSQ', // Google Analytics tracking ID (Local Dev)
    },

    /**
     * Provides the CSS breakpoints - Should match the breakpoints defined in the *.scss files.
     */
    cssBreakpoints: {
        $small: 480,
        $mid: 770,
        $wide: 1024,
        $wider: 1320,
        $superwide: 1800,
    },

    default: {
        externalFontCss: 'https://fonts.googleapis.com/css?family=Mulish',
        backHeaderButtonStyle: 'cancel',
        lowerBudgetChoice: 5000,
        midBudgetChoice: 10000,
        policyDate: '06/07/2023',
        registerInterestAt: 'https://www.ecofurb.com/contact/',
        showEvaluationConsent: false,
        showMarketingConsent: true,
    },

    /**
     * Set enabled to true here to force any scheme to use the specified ID - This allows the dev environment to operate with one data set.
     */
    forceDevSchemeData: {
        enabled: false,
        id: 1,
    },

    recaptcha: {
        checkboxKey: '6Lcvc9kZAAAAACnATIbxGfLLkFND_mi8kP96uLTF',
        invisibleKey: '6LdXhm4aAAAAAIgDSxEAKaWIpxP63En81ckEOeKg',
        disableOnLocal: process.env.VUE_APP_DISABLE_RECAPTCHA === 'true',
    },

    defaultPartnerGroup: {
        id: 4,
        name: 'RetrofitWorks Ltd',
    },

    /**
     * @type {number} A value in seconds to control the number of seconds between calls to load a project in the UI.
     */
    parallelLoadPreventedIfLessThan: 20,

    /**
     * Configuration mappings of theme names and urls.
     * @description The host 'DEFAULT' shows the default scheme to load when accessing with a pure IP address, which allows you to test locally using a mobile device.
     * When using the local urls, suffix with `:8080`
     * @example: 'local-ecofurb-pb.localhost:8080'
     * @type {Array<{ schemeHost: Array<SchemeHost>, theme: string, id: ?number, analyticsId: ?string, linkers: Array<string>|null }>}
     */
    schemes: [
        {
            schemeHost: [
                new SchemeHost('localhost'),
                new SchemeHost('local-pb.localhost'),
                new SchemeHost('dev.planbuilder.co.uk'),
                new SchemeHost('staging.planbuilder.co.uk'),
                new SchemeHost('www.planbuilder.co.uk'),
            ],
            theme: 'none',
        },
        {
            schemeHost: [
                new SchemeHost('local-ecofurb-pb.localhost', { [FEATURES.SHOPPING_BASKET]: true }),
                new SchemeHost('dev-ecofurb.planbuilder.co.uk', { [FEATURES.SHOPPING_BASKET]: true }),
                new SchemeHost('staging-ecofurb.planbuilder.co.uk', { [FEATURES.SHOPPING_BASKET]: true }),
                new SchemeHost('app.ecofurb.com'),
                new SchemeHost('local-ecofurb-national-pb.localhost'),
                new SchemeHost('dev-ecofurb-national.planbuilder.co.uk'),
                new SchemeHost('staging-ecofurb-national.planbuilder.co.uk'),
                new SchemeHost('preview-ecofurb-national.planbuilder.co.uk'),
                new SchemeHost('DEFAULT'),
            ],
            theme: 'ecofurb', // National
            id: 65,
            analyticsId: 'G-B3Q4K8PV40',
            useCrmTracking: true,
            linkers: [ 'ecofurb.com', 'www.ecofurb.com' ],
        },
        {
            schemeHost: [],
            theme: 'headless-ecofurb', // Headless
            id: 4,
        },
        {
            schemeHost: [
                new SchemeHost('local-ecofurb-landlords-pb.localhost', { [FEATURES.SHOPPING_BASKET]: true }),
                new SchemeHost('dev-ecofurb-landlords.planbuilder.co.uk', { [FEATURES.SHOPPING_BASKET]: true }),
                new SchemeHost('staging-ecofurb-landlords.planbuilder.co.uk', { [FEATURES.SHOPPING_BASKET]: true }),
                new SchemeHost('landlords.ecofurb.com'),
            ],
            theme: 'ecofurb-landlords',
            id: 118,
            analyticsId: 'G-WP3XTJCWPB',
            useCrmTracking: true,
            linkers: [ 'ecofurb.com', 'www.ecofurb.com' ],
        },
        {
            schemeHost: [
                new SchemeHost('local-energyathome-pb.localhost'),
                new SchemeHost('dev-energyathome.planbuilder.co.uk'),
                new SchemeHost('staging-energyathome.planbuilder.co.uk'),
                new SchemeHost('energyathome.planbuilder.co.uk'),
            ],
            theme: 'energyathome', // Lite
            id: 66,
            analyticsId: 'G-D9WH706NR7',
            linkers: [ 'energyathome.org.uk', 'www.energyathome.org.uk', 'bathnes.gov.uk', 'www.bathnes.gov.uk' ],
        },
        {
            schemeHost: [
                new SchemeHost('local-energysavingdevon-pb.localhost'),
                new SchemeHost('dev-energysavingdevon.planbuilder.co.uk'),
                new SchemeHost('staging-energysavingdevon.planbuilder.co.uk'),
                new SchemeHost('energysavingdevon.planbuilder.co.uk'),
            ],
            theme: 'energysavingdevon', // Lite
            id: 57,
            analyticsId: 'G-PPYXWPHEXF',
            linkers: [ 'energysavingdevon.org.uk', 'www.energysavingdevon.org.uk' ],
        },
        {
            schemeHost: [
                new SchemeHost('local-energysaving-pb.localhost'),
                new SchemeHost('dev-energysaving.planbuilder.co.uk'),
                new SchemeHost('staging-energysaving.planbuilder.co.uk'),
                new SchemeHost('energysaving.planbuilder.co.uk'),
            ],
            theme: 'energy-saving-west-sussex', // Lite
            id: 9,
            analyticsId: 'G-7NFY235M58',
            linkers: [ 'energysavingwestsussex.org.uk', 'www.energysavingwestsussex.org.uk' ],
        },
        {
            schemeHost: [
                new SchemeHost('local-yourhomebetter-pb.localhost'),
                new SchemeHost('dev-yourhomebetter.planbuilder.co.uk'),
                new SchemeHost('staging-yourhomebetter.planbuilder.co.uk'),
                new SchemeHost('yourhomebetter.planbuilder.co.uk'),
            ],
            theme: 'gmca', // Pro
            id: 13,
            analyticsId: 'G-DE93M4M4QY',
            linkers: [ 'yourhomebetter.co.uk', 'www.yourhomebetter.co.uk' ],
        },
        {
            schemeHost: [
                new SchemeHost('local-cosyhomeslancs-pb.localhost'),
                new SchemeHost('dev-cosyhomeslancs.planbuilder.co.uk'),
                new SchemeHost('staging-cosyhomeslancs.planbuilder.co.uk'),
                new SchemeHost('cosyhomeslancs.planbuilder.co.uk'),
            ],
            theme: 'lancashire', // Pro
            id: 38,
            analyticsId: 'G-WHHE23SGW4',
            linkers: [ 'www.chil.uk.com', 'www.rheaprojects.co.uk' ],
        },
        {
            schemeHost: [
                new SchemeHost('local-cosyhomesnewforest-pb.localhost'),
                new SchemeHost('dev-cosyhomesnewforest.planbuilder.co.uk'),
                new SchemeHost('staging-cosyhomesnewforest.planbuilder.co.uk'),
                new SchemeHost('cosyhomesnewforest.planbuilder.co.uk'),
            ],
            theme: 'newforest', // Lite
            id: 89,
            analyticsId: 'G-X77XSLGLW2',
            linkers: [ 'cosyhomesnewforest.org.uk', 'www.cosyhomesnewforest.org.uk', 'www.newforest.gov.uk' ],
        },
        {
            schemeHost: [
                new SchemeHost('local-northumberland-pb.localhost'),
                new SchemeHost('dev-northumberland.planbuilder.co.uk'),
                new SchemeHost('staging-northumberland.planbuilder.co.uk'),
                new SchemeHost('northumberland.planbuilder.co.uk'),
            ],
            theme: 'northumberland', // Lite
            id: 91,
            analyticsId: 'G-1X1WX6SHTY',
            linkers: [ 'northumberland.gov.uk', 'www.northumberland.gov.uk' ],
        },
        {
            schemeHost: [
                new SchemeHost('local-cosyhomesoxfordshire-pb.localhost'),
                new SchemeHost('dev-cosyhomesoxfordshire.planbuilder.co.uk'),
                new SchemeHost('staging-cosyhomesoxfordshire.planbuilder.co.uk'),
                new SchemeHost('app.cosyhomesoxfordshire.org'),
            ],
            theme: 'oxfordshire', // Pro
            id: 5,
            analyticsId: 'G-RBDFK4W3FD',
            linkers: [ 'cosyhomesoxfordshire.org', 'www.cosyhomesoxfordshire.org' ],
        },
        {
            schemeHost: [
                new SchemeHost('local-theretrofitcentre-pb.localhost'),
                new SchemeHost('dev-theretrofitcentre.planbuilder.co.uk'),
                new SchemeHost('staging-theretrofitcentre.planbuilder.co.uk'),
                new SchemeHost('theretrofitcentre.planbuilder.co.uk'),
            ],
            theme: 'theretrofitcentre', // Lite
            id: 64,
            analyticsId: 'G-JTZR50C1L0',
            linkers: [ 'theretrofitcentre.org', 'www.theretrofitcentre.org', 'stroud.gov.uk', 'www.stroud.gov.uk' ],
        },
        {
            schemeHost: [
                new SchemeHost('local-retrofit-west-advice-pb.localhost'),
                new SchemeHost('dev-retrofit-west-advice.planbuilder.co.uk'),
                new SchemeHost('staging-retrofit-west-advice.planbuilder.co.uk'),
                new SchemeHost('retrofit-west-advice.planbuilder.co.uk'),
            ],
            theme: 'retrofit-west', // Lite
            id: 71,
            analyticsId: 'G-QPB9708LK8',
            linkers: [ 'www.futureproof.uk.net', 'www.cse.org.uk', 'www.westofengland-ca.gov.uk' ],
        },
        {
            schemeHost: [
                new SchemeHost('local-shropshire-pb.localhost'),
                new SchemeHost('dev-shropshire.planbuilder.co.uk'),
                new SchemeHost('staging-shropshire.planbuilder.co.uk'),
                new SchemeHost('shropshire.planbuilder.co.uk'),
                new SchemeHost('shropshire-planbuilder.parityprojects.com'),
            ],
            theme: 'shropshire', // Lite
            id: 3,
            analyticsId: 'G-FZ5L3NLMHM',
            linkers: [ 'shropshire-planbuilder.parityprojects.com' ],
        },
        {
            schemeHost: [
                new SchemeHost('local-warmersussex-pb.localhost'),
                new SchemeHost('dev-warmersussex.planbuilder.co.uk'),
                new SchemeHost('staging-warmersussex.planbuilder.co.uk'),
                new SchemeHost('app.warmersussex.co.uk'),
            ],
            theme: 'warmersussex', // Pro
            id: 6,
            analyticsId: 'G-M1YLMF4SK4',
            linkers: [ 'warmersussex.co.uk', 'www.warmersussex.co.uk' ],
        },
        {
            schemeHost: [
                new SchemeHost('local-wokinghamenergy-pb.localhost'),
                new SchemeHost('dev-wokinghamenergy.planbuilder.co.uk'),
                new SchemeHost('staging-wokinghamenergy.planbuilder.co.uk'),
                new SchemeHost('wokinghamenergy.planbuilder.co.uk'),
            ],
            theme: 'wokingham', // Lite
            id: 80,
            analyticsId: 'G-GWB9XEMSJ0',
            linkers: [ 'wokingham.gov.uk', 'www.wokingham.gov.uk' ],
        },
        {
            schemeHost: [
                new SchemeHost('local-hugapply-pb.localhost'),
                new SchemeHost('dev-hugapply.planbuilder.co.uk'),
                new SchemeHost('staging-hugapply.planbuilder.co.uk'),
                new SchemeHost('hugapply.planbuilder.co.uk'),
            ],
            theme: 'hugapply', // Umbrella
            id: 78,
            analyticsId: 'G-W468V7EQQE',
            linkers: [ 'hugapply.co.uk' ],
        },
        {
            schemeHost: [],
            theme: 'headless-eon', // Headless
            id: 92,
        },
        {
            schemeHost: [],
            theme: 'headless-warmworks', // Headless
            id: 93,
        },
        {
            schemeHost: [],
            theme: 'headless-agilityeco', // Headless
            id: 94,
        },
        {
            schemeHost: [
                new SchemeHost('local-yorenergy-pb.localhost'),
                new SchemeHost('dev-yorenergy.planbuilder.co.uk'),
                new SchemeHost('planbuilder-int.yorenergy.co.uk'),
                new SchemeHost('integration-yorenergy.planbuilder.co.uk'),
                new SchemeHost('planbuilder-stage.yorenergy.co.uk'),
                new SchemeHost('staging-yorenergy.planbuilder.co.uk'),
                new SchemeHost('planbuilder.yorenergy.co.uk'),
                new SchemeHost('yorenergy.planbuilder.co.uk'), // TODO - delete later
            ],
            theme: 'yorenergy', // Pro
            id: 103,
            analyticsId: 'G-9TTRLPM3HL',
        },
    ],

    /**
     * Array of partner links which can be loaded to update the service offerings for the scheme.
     * @description It is not necessary to link any Partner Link IDs here that do not have defined
     * service behaviour. To add blocked or additional services import the ServiceOffering class and
     * SERVICE_OFFERINGS keys and add them as arrays to pass to the PartnerLink constructor.
     * @example new PartnerLink(42, [ SERVICE_OFFERINGS.homeEnergyPlan ], [ new ServiceOffering('test', 'Test Service') ]),
     * @type {Array<PartnerLink>}
     */
    partnerLinks: [
        new PartnerLink(43, [ SERVICE_OFFERINGS.financeOptions ]),
    ],

    serverMaxBudget: 2147483647,

    /**
     * Define custom navigation items that are used across all schemes.
     * @example new NavigationLink('Parity Projects', 'http://www.parityprojects.com', { order: 100 }
     * @type {Array<NavigationLink>}
     */
    sharedNavigationItems: [ ],

    /**
     * Define in which circumstances System Generated Error reports should be sent.
     */
    systemGeneratedErrors: {
        incompatibleImprovements: false,
    },

    /**
     * Can be to true to show a test error in the error handler for debug purposes.
     */
    test: {
        showError: false,
    },

    /**
     * Can be to true to show detailed logging on data rules for debug purposes.
     */
    verboseRuleLogging: false,
}
